import React from 'react';
import styled from 'styled-components/macro';
import first from 'lodash.first';

import { Link } from 'components/data-containers/PositionsTable/shared/PositionsTable.style';
import WithLabel from 'components/data-visualization/Table/WithLabel';

import formatNumber from 'helpers/formatNumber';
import getShortName from 'helpers/getShortName';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import CountryFlag from 'components/base/CountryFlag/index';
import PositionArrow from 'components/utility/PositionArrow/PositionArrow';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { FixedWidthPosition } from '../shared/Charts/styles';
import theme from 'styles/themes/variables';

import { TableRow as BreakdownTableRow } from 'components/Overview/BreakdownTable/styles';
import { ProductTypes } from 'types/shared';
import { MAX_COUNTRY_NAME } from 'config';

const TableRow = styled(BreakdownTableRow)`
  @media screen and (max-width: 50em) {
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid ${({ theme }): string => theme.Grey};

    td {
      flex: 1 1 50%;

      &[data-product-column] {
        order: 3;
      }
    }
  }
`;

interface IOfficialTableRowProps {
  chart: any;
  productType: ProductTypes;
  isSmallScreen: boolean;
  showProductColumn: boolean;
}

const OfficialTableRow: React.FC<IOfficialTableRowProps> = (props) => {
  const { chart, productType, isSmallScreen, showProductColumn } = props;

  const entityType = apiTypeToEntityType(chart.productType);

  const {
    productDisplayName,
    productId,
    chartDisplayName,
    currentPosition,
    customerName,
  } = chart;

  const territory = first<any>(chart.territoryData);
  const territoryDisplayName = isSmallScreen
    ? territory.id
    : territory.displayName || 'Global';
  const previousPosition = territory.previousPosition;
  const territoryName = getShortName(territoryDisplayName);

  return (
    <TableRow>
      <td>
        <FixedWidthPosition margin={`0 ${theme.spacing.xsmall} 0 0`}>
          {formatNumber(currentPosition, 'position')}
        </FixedWidthPosition>
        <PositionArrow
          position={currentPosition}
          previousPosition={previousPosition}
          showTooltip={true}
        ></PositionArrow>
      </td>
      {showProductColumn && (
        <td>
          <WithLabel
            shouldDisplayLabel={isSmallScreen}
            label={`${productType ? productType : ''} name`}
          >
            <Link to={`/entity/${entityType}/${productId}/charts`}>
              {productDisplayName}
            </Link>
          </WithLabel>
        </td>
      )}
      <td>
        <WithLabel shouldDisplayLabel={isSmallScreen} label="chart">
          {chartDisplayName}
        </WithLabel>
      </td>
      <td>{customerName}</td>
      <td>
        <TooltipWrapper
          condition={territoryDisplayName?.length > MAX_COUNTRY_NAME}
          tooltipContent={territoryDisplayName}
        >
          <WithLabel shouldDisplayLabel={isSmallScreen} label="territory">
            <CountryFlag
              id={territory.countryId}
              displayName={territory.displayName}
              flagImgSmall={territory.flagImgSmall}
              flagImgMedium={territory.flagImgMedium}
              flagImgLarge={territory.flagImgLarge}
              small
            />
            <span>
              {territory.countryId === 'WW' ? 'Global' : territoryName}
            </span>
          </WithLabel>
        </TooltipWrapper>
      </td>
    </TableRow>
  );
};

export default OfficialTableRow;
